import React from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  addDays,
  isAfter,
  isBefore,
} from 'date-fns';
import './Calendar.scss';
import TimeSelector from './TimeSelector';
import { TimeValue } from './types';

export type CalendarMode = 'start' | 'end' | 'both';

interface CalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  hoveredDate: Date | null;
  startDisplayDate: Date;
  onDateClick: (date: Date) => void;
  onDateHover: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  showTimeInputs?: boolean;
  mode?: CalendarMode;
  startTime?: TimeValue;
  endTime?: TimeValue;
  onStartTimeChange?: (time: TimeValue) => void;
  onEndTimeChange?: (time: TimeValue) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  startDate,
  endDate,
  hoveredDate,
  startDisplayDate,
  onDateClick,
  onDateHover,
  onMonthChange,
  mode = 'both',
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  showTimeInputs = true,
}) => {
  const handlePrevMonth = () => {
    onMonthChange(subMonths(startDisplayDate, 1));
  };

  const handleNextMonth = () => {
    onMonthChange(addMonths(startDisplayDate, 1));
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate) return false;
    if (!isSameMonth(date, startDisplayDate)) return false;

    if (endDate) {
      return isWithinInterval(date, {
        start: startDate,
        end: endDate,
      });
    }
    if (hoveredDate) {
      const start = isBefore(startDate, hoveredDate) ? startDate : hoveredDate;
      const end = isAfter(startDate, hoveredDate) ? startDate : hoveredDate;
      return isWithinInterval(date, { start, end });
    }
    return isSameDay(date, startDate);
  };

  const isStartDate = (date: Date): boolean => {
    return startDate ? isSameDay(date, startDate) : false;
  };

  const isEndDate = (date: Date): boolean => {
    return endDate ? isSameDay(date, endDate) : false;
  };

  // Helper to determine if a date is at the start of a range within its week
  const isStartOfRangeInWeek = (date: Date, weekStart: Date): boolean => {
    if (!startDate) return false;

    // Only consider dates in the current month for highlighting
    if (!isSameMonth(date, startDisplayDate)) return false;

    // If it's the start date itself
    if (isStartDate(date)) return true;

    // If it's the first day of the week and in the range
    if (isSameDay(date, weekStart) && isDateInRange(date)) {
      // Check if the previous day is not in range
      const prevDay = addDays(date, 1);
      return !isDateInRange(prevDay);
    }

    // If the previous day is not in range but this day is
    const prevDay = addDays(date, 1);
    return isDateInRange(date) && !isDateInRange(prevDay);
  };

  // Helper to determine if a date is at the end of a range within its week
  const isEndOfRangeInWeek = (date: Date, weekEnd: Date): boolean => {
    if (!startDate) return false;

    // Only consider dates in the current month for highlighting
    if (!isSameMonth(date, startDisplayDate)) return false;

    // If it's the end date itself
    if (isEndDate(date)) return true;

    // If it's the last day of the week and in the range
    if (isSameDay(date, weekEnd) && isDateInRange(date)) {
      // Check if the next day is not in range
      const nextDay = addDays(date, 1);
      return !isDateInRange(nextDay);
    }

    // If the next day is not in range but this day is
    const nextDay = addDays(date, 1);
    return isDateInRange(date) && !isDateInRange(nextDay);
  };

  const renderDays = () => {
    const days = [];
    const monthStart = startOfMonth(startDisplayDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'];

    // Render week day headers
    const weekDayHeaders = (
      <div className='calendar-week-header'>
        {weekDays.map((day, index) => (
          <div key={index} className='calendar-weekday'>
            <span className='weekday-text'>{day}</span>
          </div>
        ))}
      </div>
    );
    days.push(weekDayHeaders);

    let day = startDate;
    while (day <= endDate) {
      const weekStart = day;
      const weekEnd = addDays(weekStart, 6);
      const week = [];

      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isSelected = isDateInRange(day);
        const isStart = isStartDate(day);
        const isEnd = isEndDate(day);
        const isStartOfRange = isStartOfRangeInWeek(day, weekStart);
        const isEndOfRange = isEndOfRangeInWeek(day, weekEnd);
        const hasEvent = [10, 22, 24].includes(parseInt(format(day, 'd')));

        let rangeClasses = '';
        if (isSelected || isStart) {
          if (isStart && isEnd) {
            // Single day selection
            rangeClasses = 'date-selected-single';
          } else if (isStart) {
            // Start of range
            rangeClasses = 'date-selected-start';
          } else if (isEnd) {
            // End of range
            rangeClasses = 'date-selected-end';
          } else if (isStartOfRange && isEndOfRange) {
            // Complete range within a single week
            rangeClasses = 'date-in-range-full';
          } else if (isStartOfRange) {
            // Start of range within week
            rangeClasses = 'date-in-range-start';
          } else if (isEndOfRange) {
            // End of range within week
            rangeClasses = 'date-in-range-end';
          } else {
            // Middle of range
            rangeClasses = 'date-in-range';
          }
        }

        week.push(
          <div
            key={day.toString()}
            className={`calendar-day ${rangeClasses}`}
            onClick={() => onDateClick(cloneDay)}
            onMouseEnter={() => onDateHover(cloneDay)}
          >
            <span
              className={`day-text ${isCurrentMonth ? 'current-month' : 'other-month'} ${isStart || isEnd ? 'selected-day-text' : ''}`}
            >
              {format(day, 'd')}
            </span>
            {hasEvent && <div className='event-indicator' />}
          </div>,
        );
        day = addDays(day, 1);
      }
      days.push(
        <div key={weekStart.toString()} className='calendar-week'>
          {week}
        </div>,
      );
    }

    return days;
  };

  const defaultTimeChangeHandler = () => {
    console.warn('Time change handler is not provided.');
  };

  return (
    <div className='calendar-container'>
      <div className='calendar-wrapper'>
        <div className='calendar-content'>
          <div className='calendar-header'>
            <button analytics-id='calendar-prev-month-btn' className='month-nav-button' onClick={handlePrevMonth}>
              <div className='prev-icon'></div>
            </button>
            <span className='month-title'>{format(startDisplayDate, 'MMMM yyyy')}</span>
            <button analytics-id='calendar-next-month-btn' className='month-nav-button' onClick={handleNextMonth}>
              <div className='next-icon'></div>
            </button>
          </div>
          <div className='calendar-grid'>{renderDays()}</div>
          {mode && showTimeInputs && (
            <div className='time-selector-wrapper'>
              <TimeSelector
                mode={mode}
                startTime={startTime || { hours: '12', minutes: '00', seconds: '00', period: 'AM' }}
                endTime={endTime || { hours: '12', minutes: '00', seconds: '00', period: 'AM' }}
                onStartTimeChange={onStartTimeChange || defaultTimeChangeHandler}
                onEndTimeChange={onEndTimeChange || defaultTimeChangeHandler}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
